import { useMemo } from 'react';
import { NodeMetricsSchema } from '@/domain/metrics/contracts';
import { runDiagnosis } from '@/domain/diagnosis/engine';
import { buildDiagnosisReport } from '@/domain/diagnosis/report';
import { APPROVED_SCORE_CONFIG_V1 } from '@/domain/diagnosis/score-config';
import type {
  DiagnosisContext,
  DiagnosisReport,
  DiagnosisResult,
  ScoreConfig,
} from '@/domain/diagnosis/contracts';
import { getNodeDefinition } from '../nodes/registry/node-registry';
import { useEditorStore } from '../state/editor-store';
import { useSimulation } from '../simulation/use-simulation';

function targetContext(simulation: NonNullable<ReturnType<typeof useSimulation>>) {
  const target = simulation.activeScenario?.target;
  const reverse = simulation.reverse;
  if (!target || !reverse) return undefined;
  const requirements = [...reverse.requiredNodeMetrics.values()].flat();
  const status = requirements.some((item) => item.status === 'unreachable')
    ? ('unreachable' as const)
    : reverse.unresolved.length > 0 ||
        requirements.some((item) => item.status === 'unknown' || item.status === 'ambiguous')
      ? ('ambiguous' as const)
      : ('known' as const);
  const current =
    target.metric === 'clients'
      ? simulation.active.funnel.projectedClients.effectiveValue
      : target.metric === 'revenue'
        ? simulation.active.funnel.projectedRevenue.effectiveValue
        : target.nodeId
          ? simulation.active.propagation.nodeResults.get(target.nodeId)?.[target.metric]
              .effectiveValue
          : null;
  return {
    metric: target.metric,
    status,
    required: target.value,
    ...(typeof current === 'number' ? { current } : {}),
  };
}

export function useDiagnosis(
  scoreConfig: ScoreConfig = APPROVED_SCORE_CONFIG_V1,
): { result: DiagnosisResult; report: DiagnosisReport } | null {
  const graph = useEditorStore((state) => state.graph);
  const simulation = useSimulation();
  return useMemo(() => {
    if (!graph || !simulation) return null;
    const metricWarnings = simulation.active.propagation.warnings;
    const diagnosisTarget = targetContext(simulation);
    const context: DiagnosisContext = {
      graph: {
        nodes: graph.nodes.map((node) => {
          const definition = getNodeDefinition(node.type ?? '');
          const persistedMetrics = NodeMetricsSchema.safeParse(node.data.metrics);
          const roles = [
            ...new Set([
              ...(definition?.metricRoles ?? []),
              ...(persistedMetrics.success ? (persistedMetrics.data.roleOverrides ?? []) : []),
            ]),
          ];
          return {
            id: node.id,
            type: node.type ?? 'unknown',
            category: definition?.category ?? String(node.data.category ?? 'documentation'),
            status: String(node.data.status ?? 'active'),
            isTerminal: definition?.isTerminal ?? false,
            name: String(
              node.data.name ?? node.data.label ?? definition?.label ?? 'Untitled stage',
            ),
            roles,
            metrics: simulation.active.propagation.nodeResults.get(node.id)!,
          };
        }),
        edges: graph.edges.map((edge) => {
          const edgeResult = simulation.active.propagation.edgeResults.get(edge.id)!;
          return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: edge.type ?? 'standard',
            conversion: edgeResult.conversion,
            warnings: metricWarnings.filter((warning) => warning.refs?.includes(edge.id)),
          };
        }),
        warnings: metricWarnings,
      },
      funnelMetrics: simulation.active.funnel,
      basicBottlenecks: simulation.bottlenecks,
      scenario: simulation.activeScenario
        ? {
            id: simulation.activeScenario.id,
            name: simulation.activeScenario.name,
            type: simulation.activeScenario.type,
          }
        : { id: 'base', name: 'Base', type: 'base' },
      ...(diagnosisTarget ? { target: diagnosisTarget } : {}),
      ...(scoreConfig ? { scoreConfig } : {}),
    };
    const result = runDiagnosis(context);
    return { result, report: buildDiagnosisReport(context, result) };
  }, [graph, scoreConfig, simulation]);
}
